import React, { Fragment, useEffect, useState } from 'react';
import {
  Stepper, Step, StepLabel, StepContent, Button, Paper, FormControl,
  InputLabel, Select, Typography, Grid, CircularProgress,
  MenuItem, TextField,
  IconButton,
  ButtonGroup,
  Icon,
  FormGroup,
  FormControlLabel,
  Switch,
  Divider,
} from '@material-ui/core';
import * as Icons from '@material-ui/icons';
import NeokodeButton from '../components/NeokodeButton';
import { Alert, AlertTitle } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { invokeApig } from '../libs/awsLib';
import { formatMonetary } from "../libs/formatter";
import { getPlan, getInvoicing, salesMap } from "../libs/instanceHelper";
import config from '../config';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    padding: theme.spacing(2),
  },
  stepTitle: {
    marginBottom: '30px',
  },
  stepIcon: {
    '&.MuiStepIcon-active': {
      color: '#00b31f',
    },
    '&.MuiStepIcon-completed': {
      color: '#00b31f',
    },
  },
  text: {
    margin: 'revert',
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  actionsContainer: {
    marginBottom: theme.spacing(2),
  },
  resetContainer: {
    padding: theme.spacing(3),
  },
  formControl: {
    minWidth: 120,
    marginTop: '15px',
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  loadingDomains: {
    verticalAlign: 'bottom',
  },
  refreshDomainsButton: {
    paddingBottom: '5px',
    verticalAlign: 'bottom',
  },
  buttonSales: {
    display: 'block',
  },
  invocingCheck: {
    '& .Mui-checked': {
      color: '#00b31f',
      '& + .MuiSwitch-track': {
        backgroundColor: '#00b31f', // Color del track cuando está activado
      },
    },
  },
  checked: {},
  track: {},
}));

function InstanceForm(props) {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [message, setMessage] = useState('');
  const [domains, setDomains] = useState([]);
  const [uf, setUf] = useState(0);
  const [isLoadingDomains, setIsLoadingDomains] = useState(true);
  const [isErrorDomains, setIsErrorDomains] = useState(false);
  const [images, setImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(true);
  const [isErrorImages, setIsErrorImages] = useState(false);
  const [instance, setInstance] = useState({
    domain: '',
    sales: 0,
    useSubdomain: false,
    subdomainLabel: '',
    imageType: '',
  });

  useEffect(() => {
    getUf();
    getImages();
  }, []);

  const getImages = async () => {
    try {
      setIsLoadingImages(true);
      setIsErrorImages(false);
      const response = await invokeApig({
        path: '/instance/images',
      });
      if (response.status === 'OK') {
        setImages(response.result || []);
      } else {
        setIsErrorImages(true);
      }
    } catch (error) {
      console.error('Error images:', error);
      setIsErrorImages(true);
    }
    setIsLoadingImages(false);
  };

  const getUf = async () => {
    try {
      const response = await invokeApig({
        app: 'billing',
        path: `/billing/uf`
      });
      if (response.code === 0) {
        setUf(response.payload);
      } else {
        console.error('Error UF:', response);  
      }
    } catch (error) {
      console.error('Error UF:', error);
    }
  };

  const findDomains = async() => {
    try {
      setIsLoadingDomains(true);
      setIsErrorDomains(false);
      setDomains([]);
      const response = await invokeApig({
        app: "domain",
        path: "/domain/find",
        method: "POST",
      });
      if (response.status === 'OK') {
        setDomains(response.result);
        // Si tiene dominios propios, el subdominio gratuito no aplica (es excepcional).
        if (response.result && response.result.length > 0) {
          setInstance((prev) => ({ ...prev, useSubdomain: false, subdomainLabel: '' }));
        }
      } else {
        setIsErrorDomains(true);
      }
    } catch (error) {
      console.error('Error:', error);
      setIsErrorDomains(true);
    }
    setIsLoadingDomains(false);
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInstance({ ...instance, [name]: value });
    setIsError(false);
  };

  const createInstance = async () => {
    let isSuccess = false;
    try {
      setIsLoading(true);
      setIsError(false);
      const base = { plan: getPlan(instance.sales), imageType: instance.imageType };
      const request = instance.useSubdomain
        ? {
            ...base,
            useSubdomain: true,
            subdomainLabel: instance.subdomainLabel.trim().toLowerCase(),
          }
        : {
            ...base,
            domain: instance.domain,
          };
      const response = await invokeApig({
        path: "/instance",
        method: "POST",
        body: request
      });
      if (response.Status === 0) {
        isSuccess = true;
      } else {
        setMessage('No hemos podido crear el ERP, por favor intenta más tarde o contacta a Soporte');
        setIsError(true);
      }
    } catch (error) {
      setMessage('Ha ocurrido un error al crear el ERP, por favor intenta más tarde o contacta a Soporte');
      setIsError(true);
    }
    setIsLoading(false);
    return isSuccess;
  };

  const handleGoBack = (event) => {
    event.preventDefault();
    if (props.onFinish) {
      props.onFinish(false);
    } else {
      props.history.push('/instances');
    }
  }

  const handleNext = async () => {
    let isValid = true;
    switch(activeStep) {
      case 0: {
        findDomains();
        break;
      }
      case 1: {
        if (instance.useSubdomain) {
          const label = (instance.subdomainLabel || '').trim().toLowerCase();
          if (!/^[a-z0-9](?:[a-z0-9-]{0,28}[a-z0-9])?$/.test(label)) {
            setMessage('Ingresa un nombre válido para el subdominio (letras, números y guiones, 2 a 30 caracteres).');
            isValid = false;
          }
        } else if (!instance.domain) {
          setMessage('Debes seleccionar un dominio o usar un subdominio para continuar');
          isValid = false;
        }
        break;
      }
      case 2: {
        if (!instance.imageType) {
          setMessage('Debes seleccionar un tipo de negocio');
          isValid = false;
        }
        break;
      }
      case 3: {
        if (!instance.sales) {
          setMessage('Debes seleccionar un nivel de ventas anuales');
          isValid = false;
        }
        break;
      }
      case 4: {
        if (!await createInstance()) {
          isValid = false;
        }
      }
    }
    if (isValid) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
    setIsError(!isValid);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const getStepDescription = () => {
    return (
      <Alert severity="info">
        <AlertTitle>¿Estás listo para llevar tu empresa al siguiente nivel?</AlertTitle>
        <Typography className={classes.text}>
          Con nuestro ERP, puedes optimizar tus operaciones, mejorar la toma de decisiones e impulsar el crecimiento sostenible.
        </Typography>
        <Typography className={classes.text}>
          En el siguiente asistente te guiaremos para configurar tu ERP y comenzar a utilizarlo ahora.
        </Typography>
        <Typography className={classes.text}>
          Te pediremos información para la configuración inicial, este proceso tomará sólo algunos minutos. Luego configuraremos tu sistema y te entregaremos los datos de acceso para que comiences a utilizarlo.
        </Typography>
        <Typography className={classes.text}>
          Comencemos!
        </Typography>
      </Alert>
    );
  }

  const getStepDomain = () => {
    const noDomains = !isLoadingDomains && !isErrorDomains && domains.length === 0;
    return (
      <Paper className={classes.paper}>
        <Typography component="h4" className={classes.stepTitle}>
          Selecciona un dominio para configurar tu ERP
        </Typography>
        <Alert severity='info'>
          <p>Para acceder a tu ERP necesitas un dominio propio. Selecciona uno de tus dominios contratados.</p>
          <p>Si aún no tienes un dominio, puedes contratarlo en <a href={config.apps.domain} target='_blank' rel='noopener noreferrer'>{config.apps.domain} <Icons.Launch /></a>.</p>
        </Alert>

        <FormControl className={classes.formControl}>
          <InputLabel id="domain-label">Dominio</InputLabel>
          <Select
            labelId="domain-label"
            id="domain"
            name="domain"
            value={(domains.length > 0 && !instance.useSubdomain) ? instance.domain : ''}
            onChange={handleChange}
            disabled={isLoadingDomains || instance.useSubdomain}
          >
            {
              domains.map((domain, index) => (
                <MenuItem key={index} value={domain.name}>
                  {domain.name}
                </MenuItem>
              ))
            }
          </Select>
        </FormControl>
        {
          isLoadingDomains ?
          <CircularProgress className={classes.loadingDomains} />
          :
          <IconButton onClick={findDomains} className={classes.refreshDomainsButton}>
            <Icons.Refresh />
          </IconButton>
        }
        {
          isErrorDomains &&
          <Alert severity='error'>
            Ocurrió un error al obtener los dominios disponibles. Por favor intenta más tarde o comunícate con Soporte.
          </Alert>
        }

        {
          noDomains &&
          <div style={{ marginTop: '24px' }}>
            <Alert severity='warning'>
              No encontramos dominios contratados a tu nombre. Te recomendamos usar tu propio dominio para tu ERP.
            </Alert>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    className={classes.invocingCheck}
                    checked={instance.useSubdomain}
                    onChange={(e) => handleChange({ target: { name: 'useSubdomain', value: e.target.checked } })}
                  />
                }
                label="Excepcionalmente, usar un subdominio gratuito"
              />
            </FormGroup>
            {
              instance.useSubdomain &&
              <Grid container spacing={1} alignItems="flex-end">
                <Grid item>
                  <TextField
                    id="subdomainLabel"
                    name="subdomainLabel"
                    label="Nombre del subdominio"
                    value={instance.subdomainLabel}
                    onChange={handleChange}
                    placeholder="miempresa"
                  />
                </Grid>
                <Grid item>
                  <Typography>.{config.subdomainBase}</Typography>
                </Grid>
                {
                  instance.subdomainLabel &&
                  <Grid item xs={12}>
                    <small>Tu ERP quedará en: <b>https://{instance.subdomainLabel.trim().toLowerCase()}.{config.subdomainBase}</b></small>
                  </Grid>
                }
              </Grid>
            }
          </div>
        }
      </Paper>
    );
  }

  const getStepImage = () => {
    return (
      <Paper className={classes.paper}>
        <Typography component="h4" className={classes.stepTitle}>
          Selecciona el tipo de negocio
        </Typography>
        <Typography className={classes.text}>
          Cada tipo de negocio trae el ERP preconfigurado para tu rubro.
        </Typography>
        {
          isLoadingImages ?
          <CircularProgress />
          :
          isErrorImages ?
          <Alert severity='error'>No pudimos cargar los tipos de negocio. Intenta más tarde o contacta a Soporte.</Alert>
          :
          <Grid container spacing={2} style={{ marginTop: '8px' }}>
            {
              images.map((img) => {
                const selected = instance.imageType === img.type;
                return (
                  <Grid item xs={12} sm={6} md={4} key={img.type}>
                    <Paper
                      elevation={selected ? 6 : 1}
                      onClick={() => handleChange({ target: { name: 'imageType', value: img.type } })}
                      style={{ padding: '16px', cursor: 'pointer', height: '100%', border: selected ? '2px solid #00b31f' : '2px solid transparent' }}
                    >
                      { img.icon &&
                        <img src={img.icon} alt={img.name} style={{ height: 48, marginBottom: 8 }} onError={(e) => { e.target.style.display = 'none'; }} />
                      }
                      <Typography variant="subtitle1"><b>{img.name}</b></Typography>
                      <Typography variant="body2" color="textSecondary">{img.desc}</Typography>
                    </Paper>
                  </Grid>
                );
              })
            }
            {
              images.length === 0 &&
              <Grid item xs={12}>
                <Alert severity='info'>No hay tipos de negocio disponibles por ahora. Contacta a Soporte.</Alert>
              </Grid>
            }
          </Grid>
        }
      </Paper>
    );
  }

  const getStepSales = () => {
    return (
      <Paper className={classes.paper}>
        <Typography component="h4" className={classes.stepTitle}>
          Ventas anuales
        </Typography>
        <ButtonGroup size="small" aria-label="small outlined button group">
          {
            salesMap.map((saleMap, index) => {
              return (
                instance.sales === saleMap.id ?
                <NeokodeButton key={'sale-active-' + index} className={classes.buttonSales} onClick={() => handleChange({target: {name: 'sales', value: saleMap.id}})}>
                  {saleMap.desc}
                  {uf > 0 && <small><br/>(Aprox. {saleMap.max === -1 ? 'desde': 'hasta'} {formatMonetary({value: uf * saleMap.max})})</small>}
                </NeokodeButton>
                :
                <Button key={'sale-' + index} className={classes.buttonSales} onClick={() => handleChange({target: {name: 'sales', value: saleMap.id}})}>
                  {saleMap.desc}
                  {uf > 0 && <small><br/>(Aprox. {saleMap.max === -1 ? 'desde': 'hasta'} {formatMonetary({value: uf * saleMap.max})})</small>}
                </Button>
              )
            })
          }
        </ButtonGroup>
      </Paper>
    );
  }

  const getStepConfirm = () => {
    return (
      <Paper className={classes.paper}>
        <Typography component="h4" className={classes.stepTitle}>
          Por favor confirma los datos antes de contratar el ERP
        </Typography>
        <Grid container >
          <Grid item xs><b>{instance.useSubdomain ? 'Subdominio' : 'Dominio'}</b></Grid>
          <Grid item md={8}>{
            instance.useSubdomain
              ? `${(instance.subdomainLabel || '').trim().toLowerCase()}.${config.subdomainBase}`
              : instance.domain
          }</Grid>
        </Grid>
        <Divider variant="fullWidth" />
        <Grid container >
          <Grid item md={4}><b>Tipo de negocio</b></Grid>
          <Grid item md={8}>{(images.find((i) => i.type === instance.imageType) || {}).name || instance.imageType}</Grid>
        </Grid>
        <Divider variant="fullWidth" />
        <Grid container >
          <Grid item md={4}><b>Ventas anuales</b></Grid>
          <Grid item md={8}>{
            salesMap.map((saleMap, index) => {
              return (instance.sales === saleMap.id) &&
                <Fragment key={index}>
                  {saleMap.desc}
                  {uf > 0 && <small><br/>(Aprox. {saleMap.max === -1 ? 'desde': 'hasta'} {formatMonetary({value: uf * saleMap.max})})</small>}
                </Fragment>
            })
          }</Grid>
        </Grid>
      </Paper>
    );
  }

  const getStepVoucher = () => {
    return (
      <Paper className={classes.paper}>
        <Typography component="h4" className={classes.stepTitle}>
          ERP contratado exitosamente.
        </Typography>
        <Alert severity="success">
          <p><b>¡Felicitaciones! Haz finalizado el proceso de contratación de tu ERP.</b></p>
          <p>
            Te damos la bienvenida a la familia de usuarios que utilizamos esta gran herramienta de gestión.
            Estás a punto de embarcarte en un viaje que transformará la forma en que tu negocio opera y crece.
          </p>
          <div>
            <b>Siguientes pasos</b>:<br/>
            <ul>
              <li>Revisa el estado de la configuración inicial en esta misma pantalla. La configuración inicial puede tardar unos minutos.</li>  
              <li>Revisa tu correo. Te notificaremos cuando finalice el proceso y te enviaremos los datos de acceso.</li>
              <li>Revisa los manuales y videos disponibles para comenzar a utilizarlo.</li>
              <li>Si lo consideras necesario coordina una capacitación.</li>
              <li>Comienza a utilizar tu ERP.</li>
            </ul>
          </div>
          <p>Estamos disponibles para apoyarte en lo que necesites.</p>
        </Alert>
      </Paper>
    );
  }

  const renderStep = ({label, body}) => (
    <Step>
      <StepLabel StepIconProps={{ classes: { root: classes.stepIcon } }}>{label}</StepLabel>
      <StepContent>
        { body }
        {
          isError &&
          <Alert severity="error">
            { message }
          </Alert>
        }
        <div className={classes.actionsContainer}>
            {
              activeStep > 4 ?
              <NeokodeButton
                variant="contained"
                onClick={handleGoBack}
                className={classes.button}
              >
                Ir a la lista de Sistemas ERP
              </NeokodeButton>
              :
              <div>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  className={classes.button}
                >
                  Atrás
                </Button>
                <NeokodeButton
                  variant="contained"
                  onClick={handleNext}
                  className={classes.button}
                  disabled={isLoading}
                >
                  {activeStep === 4 ? (isLoading ? <CircularProgress /> : 'Contratar') : 'Siguiente'}
                </NeokodeButton>
              </div>
            }
        </div>
      </StepContent>
    </Step>
  );

  return (
    <div className={classes.root}>
      <Stepper activeStep={activeStep} orientation="vertical">
        { renderStep({ label: 'Descripción del servicio', body: getStepDescription(), }) }
        { renderStep({ label: 'Dominio', body: getStepDomain(), }) }
        { renderStep({ label: 'Tipo de negocio', body: getStepImage(), }) }
        { renderStep({ label: 'Ventas de la empresa', body: getStepSales(), }) }
        { renderStep({ label: 'Confirmación', body: getStepConfirm(), }) }
        { renderStep({ label: 'Comprobante', body: getStepVoucher(), }) }
      </Stepper>
    </div>
  );
}

export default InstanceForm;